import React, { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import Humanizer from "@/components/Humanizer";
import { Link } from "react-router-dom";
import { Progress } from "@/components/ui/progress";
import { Zap, ArrowRight, Chrome } from "lucide-react";

const PLAN_LIMITS = { free: 1500, pro: 60000, premium: 300000 };

export default function Dashboard() {
  const { user } = useAuth();
  const [styles, setStyles] = useState([]);
  const [recent, setRecent] = useState([]);

  useEffect(() => {
    api.get("/styles").then(({ data }) => setStyles(data)).catch(() => {});
    api.get("/history").then(({ data }) => setRecent(data.slice(0, 3))).catch(() => {});
  }, []);

  if (!user) return null;
  const limit = PLAN_LIMITS[user.plan] || 1500;
  const pct = Math.min(100, Math.round((user.words_used_month / limit) * 100));

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-10 py-10">
      <div className="flex items-end justify-between flex-wrap gap-4 mb-8">
        <div>
          <div className="label-eyebrow mb-2">Dashboard</div>
          <h1 className="font-display text-4xl sm:text-5xl tracking-tight">Hey, {user.name.split(" ")[0]}.</h1>
        </div>
        <Link to="/pricing" data-testid="dashboard-upgrade" className="btn-accent flex items-center gap-2 text-sm">
          <Zap size={14} /> {user.plan === "free" ? "Upgrade plan" : "Manage plan"}
        </Link>
      </div>

      <div className="surface p-5 mb-6 flex items-center justify-between gap-4 flex-wrap bg-[hsl(var(--secondary))] border-[hsl(var(--primary))]/20">
        <div className="flex items-center gap-3">
          <Chrome size={20} className="text-[hsl(var(--primary))]" />
          <div>
            <div className="font-semibold text-sm">Humanize inside Gmail, Notion, and LinkedIn</div>
            <div className="text-xs text-[hsl(var(--muted-foreground))]">Install the Chrome extension and rewrite text wherever you write.</div>
          </div>
        </div>
        <a
          href="/humanly-extension.zip"
          data-testid="dashboard-extension-download"
          className="btn-ghost text-sm py-2 px-4"
        >
          Download extension
        </a>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-10">
        <div className="surface p-6">
          <div className="label-eyebrow mb-2">Plan</div>
          <div className="font-display text-2xl capitalize">{user.plan}</div>
          <div className="text-xs text-[hsl(var(--muted-foreground))] mt-1">Status: {user.plan_status}</div>
        </div>
        <div className="surface p-6" data-testid="usage-card">
          <div className="label-eyebrow mb-2">Words this month</div>
          <div className="font-display text-2xl">{user.words_used_month.toLocaleString()} / {limit.toLocaleString()}</div>
          <Progress value={pct} className="mt-3 h-2" />
        </div>
        <div className="surface p-6">
          <div className="label-eyebrow mb-2">Style profiles</div>
          <div className="font-display text-2xl">{styles.length}</div>
          <Link to="/profile" className="text-xs link-underline text-[hsl(var(--muted-foreground))] mt-2 inline-block">Manage →</Link>
        </div>
      </div>

      <Humanizer styleProfiles={styles} />

      <div className="mt-12">
        <div className="flex items-center justify-between mb-6">
          <div className="label-eyebrow">Recent rewrites</div>
          <Link to="/history" data-testid="dashboard-history-link" className="link-underline text-sm text-[hsl(var(--primary))] font-semibold flex items-center gap-1">
            View all <ArrowRight size={12} />
          </Link>
        </div>
        {recent.length === 0 ? (
          <div className="surface p-8 text-sm text-[hsl(var(--muted-foreground))]">
            No rewrites yet. Try humanizing something above.
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {recent.map((h) => (
              <div key={h.id} data-testid={`recent-${h.id}`} className="surface p-5">
                <div className="text-xs text-[hsl(var(--muted-foreground))]">
                  {new Date(h.created_at).toLocaleString()} · {h.tone}
                </div>
                <div className="mt-3 text-sm line-clamp-4">{h.humanized}</div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
